/**
 * A emissão que ficou pela metade, gravada para sobreviver ao fechamento
 * da aba — porta `cteatf_retomada.dart`.
 *
 * O servidor deduplica o CT-e pela chave de idempotência e pelo transportador, e a
 * chave vive no navegador. Sem o registro, recarregar a página depois da SEFAZ faz
 * o próximo envio gerar chave nova e emitir **outro** CT-e para a mesma viagem.
 * Por isso o registro leva tudo o que concluir exige — o corpo da ATF e o
 * cooperado —, e mora numa chave por cooperado: o navegador pode ser de dois.
 */

const VALIDADE_MS = 24 * 60 * 60 * 1000;
const PREFIXO_STORAGE = 'tresco_cooperado_emissao_em_curso';

function chaveDoStorage(idCooperado: number): string {
  return `${PREFIXO_STORAGE}:${idCooperado}`;
}

export interface EmissaoEmCurso {
  numCte: string;
  keyCte?: string;
  keyAtf?: string;
  idCte?: number;
  serie?: string;
  /** A SEFAZ já autorizou — o retry pula direto para a ATF. */
  cteAutorizado: boolean;
  /** Identidade congelada da tentativa — o índice de idempotência do backend é por (chave, transportador). */
  idMotorista?: number;
  idVeiculoSgtf?: string;
  placa?: string;
  coberturaKey?: string;
  /** O corpo do POST /atf solicitar da tentativa ORIGINAL — nunca remontado do formulário atual. */
  corpoAtf?: Record<string, unknown>;
  /** O que a tela mostra para o cooperado reconhecer a viagem. */
  resumo?: ResumoDaViagem;
  ts: number;
}

export interface ResumoDaViagem {
  origem: string;
  destino: string;
  dataIda: string;
}

/** O que a emissão sabe depois de o CT-e existir. */
export interface TentativaDeEmissao {
  numCte: string;
  keyCte: string | null;
  keyAtf: string | null;
  idCte: number | null;
  serie: string | null;
  cteAutorizado: boolean;
}

function textoOuNull(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s === '' ? undefined : s;
}

function inteiroOuNull(v: unknown): number | undefined {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseInt(v.trim(), 10);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** Nunca lança — registro corrompido ou de versão antiga vira `null`. */
export function parseEmissaoEmCurso(j: unknown): EmissaoEmCurso | null {
  if (j == null || typeof j !== 'object') return null;
  const o = j as Record<string, unknown>;
  const numCte = textoOuNull(o.numCte);
  const ts = inteiroOuNull(o.ts);
  if (!numCte || ts == null) return null;
  return {
    numCte,
    keyCte: textoOuNull(o.keyCte),
    keyAtf: textoOuNull(o.keyAtf),
    idCte: inteiroOuNull(o.idCte),
    serie: textoOuNull(o.serie),
    cteAutorizado: o.cteAutorizado === true,
    idMotorista: inteiroOuNull(o.idMotorista),
    idVeiculoSgtf: textoOuNull(o.idVeiculoSgtf),
    placa: textoOuNull(o.placa),
    coberturaKey: textoOuNull(o.coberturaKey),
    corpoAtf: (o.corpoAtf != null && typeof o.corpoAtf === 'object') ? o.corpoAtf as Record<string, unknown> : undefined,
    resumo: parseResumo(o.resumo),
    ts,
  };
}

function parseResumo(v: unknown): ResumoDaViagem | undefined {
  if (v == null || typeof v !== 'object') return undefined;
  const o = v as Record<string, unknown>;
  const origem = textoOuNull(o.origem);
  const destino = textoOuNull(o.destino);
  const dataIda = textoOuNull(o.dataIda);
  return origem && destino && dataIda ? { origem, destino, dataIda } : undefined;
}

/** O registro que se grava a cada passo da emissão, a partir do CT-e criado. */
export function registroDaTentativa(
  t: TentativaDeEmissao,
  idCooperado: number,
  corpoAtf: Record<string, unknown>,
  agora: number,
  resumo?: ResumoDaViagem,
): EmissaoEmCurso {
  return {
    numCte: t.numCte,
    keyCte: t.keyCte ?? undefined,
    keyAtf: t.keyAtf ?? undefined,
    idCte: t.idCte ?? undefined,
    serie: t.serie ?? undefined,
    cteAutorizado: t.cteAutorizado,
    idMotorista: idCooperado,
    idVeiculoSgtf: textoOuNull(corpoAtf.idVeiculoSgtf),
    placa: textoOuNull(corpoAtf.placa),
    corpoAtf,
    resumo,
    ts: agora,
  };
}

export function estaValida(e: EmissaoEmCurso, agora: number): boolean {
  return agora - e.ts < VALIDADE_MS;
}

/** Dá para concluir sem remontar nada do formulário atual? */
export function podeConcluir(e: EmissaoEmCurso): boolean {
  return e.corpoAtf != null && e.idMotorista != null && e.numCte !== '';
}

/**
 * A emissão parada deste cooperado, se der para concluí-la. Registro vencido, de
 * outro cooperado ou incompleto sai do navegador: oferecê-lo levaria a um envio
 * que não tem como terminar.
 */
export function lerEmissaoEmCurso(idCooperado: number, agora: number): EmissaoEmCurso | null {
  try {
    const chave = chaveDoStorage(idCooperado);
    const bruto = localStorage.getItem(chave);
    if (!bruto) return null;
    const parsed = parseEmissaoEmCurso(JSON.parse(bruto));
    if (!parsed || !estaValida(parsed, agora) || parsed.idMotorista !== idCooperado || !podeConcluir(parsed)) {
      localStorage.removeItem(chave);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function gravarEmissaoEmCurso(idCooperado: number, e: EmissaoEmCurso): void {
  try {
    localStorage.setItem(chaveDoStorage(idCooperado), JSON.stringify(e));
    // A chave única de antes da 0094 guardava registros que nunca se concluíam.
    localStorage.removeItem(PREFIXO_STORAGE);
  } catch {
    // localStorage indisponível (modo privado etc.) — perde só a retomada, não a emissão em si.
  }
}

export function limparEmissaoEmCurso(idCooperado: number): void {
  try {
    localStorage.removeItem(chaveDoStorage(idCooperado));
  } catch {
    // idem
  }
}
